/**
 * How you trade (#777). Every number and rule shown here is read from the code the order ticket uses
 * (TIF_OPTIONS, marketBoundPx, MIN_ORDER_VALUE_USD, OUTCOME_PX_DECIMALS), so the page cannot drift from the ticket.
 */
import { useState, type ReactNode } from 'react';
import { OUTCOME_PX_DECIMALS } from '../hl/outcomes';
import { pxToCents } from '../market/format';
import { marketBoundPx, TIF_OPTIONS, type TicketTif } from '../ticket/order';
import { MIN_ORDER_VALUE_USD } from '../trading/rules';
import { localizeNumber } from '../i18n/format';
import Msg from '../i18n/Msg';
import { useT } from '../i18n/useT';

const TICK_PX = `0.${'0'.repeat(OUTCOME_PX_DECIMALS - 1)}1`;

function Rule({ title, children }: { title: string; children: ReactNode }) {
  return (
    <div className="rounded-md border border-border bg-bg/40 p-3">
      <h4 className="text-sm font-semibold">{title}</h4>
      <div className="mt-1 space-y-1 text-sm text-fg/85">{children}</div>
    </div>
  );
}

export default function TradingBasics() {
  const [tif, setTif] = useState<TicketTif>('Gtc');
  const t = useT();
  const TICK_CENTS = pxToCents(TICK_PX);
  const active = TIF_OPTIONS.find((o) => o.value === tif) ?? TIF_OPTIONS[0]!;
  return (
    <div className="space-y-4" data-testid="trading-basics">
      <div className="grid gap-3 md:grid-cols-2">
        <Rule title={t('learn.trading.limit.title')}>
          <p>{t('learn.trading.limit.p1')}</p>
          <p className="text-muted">{t('learn.trading.limit.p2')}</p>
        </Rule>
        <Rule title={t('learn.trading.open_close.title')}>
          <p>
            <Msg
              k="learn.trading.open_close.p1"
              args={{ buying: <strong>{t('learn.trading.buying')}</strong>, selling: <strong>{t('learn.trading.selling')}</strong> }}
            />
          </p>
          <p className="text-muted">{t('learn.trading.open_close.p2')}</p>
        </Rule>
        <Rule title={t('learn.trading.leverage.title')}>
          <p>{t('learn.trading.leverage.p1')}</p>
        </Rule>
        <Rule title={t('learn.trading.min.title')}>
          <p>
            <Msg
              k="learn.trading.min.p1"
              args={{ min: <span className="num">{localizeNumber(String(MIN_ORDER_VALUE_USD))} USDC</span> }}
            />
          </p>
          <p className="text-muted">
            <Msg
              k="learn.trading.min.p2"
              args={{
                lo: <span className="num">{TICK_CENTS}</span>,
                hi: <span className="num">{pxToCents(marketBoundPx(true))}</span>,
                tick: <span className="num">{TICK_CENTS}</span>,
              }}
            />
          </p>
        </Rule>
      </div>

      <div className="rounded-md border border-border bg-bg/40 p-3">
        <h4 className="text-sm font-semibold">{t('learn.trading.tif.title')}</h4>
        <div role="radiogroup" aria-label={t('ticket.tif')} className="mt-2 flex flex-wrap gap-1">
          {TIF_OPTIONS.map((o) => (
            <button
              key={o.value}
              type="button"
              role="radio"
              aria-checked={tif === o.value}
              onClick={() => setTif(o.value)}
              className={`rounded-md px-3 py-1.5 text-sm font-medium ${
                tif === o.value ? 'bg-surface-2 text-fg' : 'text-muted hover:text-fg'
              }`}
            >
              {o.label}
            </button>
          ))}
        </div>
        <p className="mt-2 text-sm" aria-live="polite" data-testid="learn-tif-help">
          {active.help}
        </p>
        {tif === 'Market' && (
          <p className="mt-1 text-sm text-muted" data-testid="learn-market-worst">
            <Msg
              k="learn.trading.market"
              args={{
                buyPx: <span className="num">{pxToCents(marketBoundPx(true))}</span>,
                sellPx: <span className="num">{pxToCents(marketBoundPx(false))}</span>,
              }}
            />
          </p>
        )}
      </div>
    </div>
  );
}
